import React, { useState, useEffect, useCallback } from 'react';
import './WeddingMain.css';
import coverImg from '../../assets/wedding/cover.png';
import introGif from '../../assets/wedding/intro.gif';
import {
  getGuestbookList,
  addGuestbook,
  deleteGuestbook,
  presignUpload,
  completeUpload,
} from '../../api/weddingApi';

// ───────────────────────────────────────────────
// 청첩장 콘텐츠 (실제 정보/문구는 여기만 수정하면 됨)
// ───────────────────────────────────────────────
const WEDDING_INFO = {
  groom: '이우용',
  bride: '김다솔',
  dateISO: '2027-05-08T13:40:00+09:00',
  dateText: '2027년 5월 8일 토요일 오후 1시 40분',
  venue: '센텀호텔 웨딩홀',
  venueHall: '4층 카카오홀',
  venueAddress: '부산 해운대구 센텀3로 20',
  // 카카오맵 퍼가기(약도) 값 — map.kakao.com 퍼가기에서 발급
  kakaoMap: { timestamp: '1782180897434', key: 'pmsm7jvhsi3' },
};

// 버전별 문구 — seal: 친한 친구용 봉인식(?invite=seal) / formal: 기본 모바일 청첩장
const COPY = {
  seal: {
    intro: true,
    coverImage: true,
    coverLabel: '✦ 봉인식 ✦',
    coverNames: [WEDDING_INFO.bride, '→', WEDDING_INFO.groom],
    coverSub: '신부 김다솔, 야생의 이우용을 봉인하다',
    greeting:
      '야생의 「이우용」이 나타났다!\n오랜 세월 누구에게도 봉인되지 않은 채\n제멋대로 떠돌던 그를,\n드디어 「김다솔」이 포획하는 데 성공했다.\n\n『나 김다솔의 이름으로 명하노라.\n야생의 이우용이여, 나의 카드가 되어라 —\n봉인(封印)!』\n\n이 역사적인 봉인식의 증인이 되어주세요.',
    ddayDone: '오늘은 봉인식 당일입니다 🔮',
    infoTitle: '봉인식 안내',
    gbPlaceholder: '봉인을 축하하는 메시지를 남겨주세요',
    gbEmpty: '첫 번째 봉인 축하 메시지를 남겨주세요.',
  },
  formal: {
    intro: false,
    coverImage: false,
    coverLabel: 'WEDDING INVITATION',
    coverNames: [WEDDING_INFO.groom, '♥', WEDDING_INFO.bride],
    coverSub: '저희 두 사람, 결혼합니다',
    greeting:
      '서로 다른 길을 걸어온 두 사람이\n이제 같은 길을 함께 걸어가려 합니다.\n\n저희의 새로운 시작을\n가까이에서 축복해 주시면\n더없는 기쁨으로 간직하겠습니다.',
    ddayDone: '오늘은 저희 결혼식 날입니다 💐',
    infoTitle: '예식 안내',
    gbPlaceholder: '축하 메시지를 남겨주세요',
    gbEmpty: '첫 번째 축하 메시지를 남겨주세요.',
  },
};

const INTRO_PLAY_MS = 10000; // 10초 (GIF 반복 재생 허용)
const INTRO_FADE_MS = 600;

// 방명록 입력 길이 제한 (백엔드 WeddingController와 동일 기준 유지)
const GB_LIMITS = { name: 20, message: 500, password: 20 };

// 이벤트 업로드 제한 (백엔드와 동일 기준)
const UPLOAD_LIMITS = { maxFiles: 20, maxSize: 1024 * 1024 * 1024 }; // 1GB/파일

function diffToTarget(targetIso) {
  const target = new Date(targetIso).getTime();
  const now = Date.now();
  const ms = Math.max(0, target - now);
  const sec = Math.floor(ms / 1000);
  return {
    done: ms === 0,
    days: Math.floor(sec / 86400),
    hours: Math.floor((sec % 86400) / 3600),
    minutes: Math.floor((sec % 3600) / 60),
    seconds: sec % 60,
  };
}

function formatDate(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}.${pad(d.getMonth() + 1)}.${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

// 공용 모달
function Modal({ open, onClose, title, children }) {
  if (!open) return null;
  return (
    <div className="wd-modal-overlay" onClick={onClose}>
      <div className="wd-modal" onClick={(e) => e.stopPropagation()}>
        <div className="wd-modal-head">
          <h3 className="wd-modal-title">{title}</h3>
          <button className="wd-modal-close" type="button" onClick={onClose} aria-label="닫기">×</button>
        </div>
        <div className="wd-modal-body">{children}</div>
      </div>
    </div>
  );
}

export default function WeddingMain({ variant = 'seal' }) {
  const copy = COPY[variant] || COPY.seal;
  const [countdown, setCountdown] = useState(() => diffToTarget(WEDDING_INFO.dateISO));

  // 인트로 GIF 오버레이 (진입 시 1회 재생 후 페이드아웃 → 본문)
  const [introVisible, setIntroVisible] = useState(copy.intro);
  const [introFading, setIntroFading] = useState(false);
  const dismissIntro = useCallback(() => {
    setIntroFading(true);
    window.setTimeout(() => setIntroVisible(false), INTRO_FADE_MS);
  }, []);

  // 모달 열림 상태
  const [guestbookOpen, setGuestbookOpen] = useState(false);
  const [eventOpen, setEventOpen] = useState(false);

  // 방명록
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ guestNm: '', message: '', password: '' });
  const [submitting, setSubmitting] = useState(false);

  // 이벤트 업로드
  const [eventForm, setEventForm] = useState({ uploaderNm: '', contact: '' });
  const [files, setFiles] = useState([]);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('');

  // 인트로 자동 종료 (한 번 재생 후)
  useEffect(() => {
    if (!copy.intro) return undefined;
    const t = window.setTimeout(dismissIntro, INTRO_PLAY_MS);
    return () => window.clearTimeout(t);
  }, [dismissIntro, copy.intro]);

  // D-day 카운트다운
  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown(diffToTarget(WEDDING_INFO.dateISO));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // 카카오맵 퍼가기(약도) 로드
  // 주의: roughmapLoader.js 는 document.write 로 lander.js 를 주입하는데, SPA(파싱 후 실행)에선
  //       document.write 가 무력화됨 → document.write 를 가로채 lander 스크립트를 DOM으로 직접 로드.
  useEffect(() => {
    const { timestamp, key } = WEDDING_INFO.kakaoMap;

    const doRender = () => {
      if (!(window.daum && window.daum.roughmap && window.daum.roughmap.Lander)) return false;
      const el = document.getElementById('daumRoughmapContainer' + timestamp);
      if (!el) return false;
      if (el.childElementCount > 0) return true; // 이미 렌더됨 (중복 방지)
      new window.daum.roughmap.Lander({
        timestamp,
        key,
        mapWidth: '100%',
        mapHeight: '360',
      }).render();
      return true;
    };

    if (doRender()) return;

    let tries = 0;
    const timer = setInterval(() => {
      tries += 1;
      if (doRender() || tries > 60) clearInterval(timer);
    }, 200);

    if (!(window.daum && window.daum.roughmap)) {
      const origWrite = document.write.bind(document);
      document.write = (html) => {
        const m = /src="([^"]+)"/.exec(html || '');
        if (m && m[1]) {
          const ls = document.createElement('script');
          ls.src = m[1];
          ls.charset = 'UTF-8';
          document.body.appendChild(ls);
        } else {
          origWrite(html);
        }
      };
      const loader = document.createElement('script');
      loader.src = 'https://ssl.daumcdn.net/dmaps/map_js_init/roughmapLoader.js';
      loader.charset = 'UTF-8';
      loader.onload = () => { document.write = origWrite; };
      loader.onerror = () => { document.write = origWrite; };
      document.body.appendChild(loader);
    }

    return () => clearInterval(timer);
  }, []);

  const loadGuestbook = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getGuestbookList();
      setEntries(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error('방명록 조회 실패', e);
    } finally {
      setLoading(false);
    }
  }, []);

  // 방명록 모달 열 때 목록 로드
  useEffect(() => {
    if (guestbookOpen) loadGuestbook();
  }, [guestbookOpen, loadGuestbook]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.guestNm.trim() || !form.message.trim()) {
      alert('이름과 메시지를 입력해주세요.');
      return;
    }
    if (form.guestNm.trim().length > GB_LIMITS.name) {
      alert(`이름은 ${GB_LIMITS.name}자 이하로 입력해주세요.`);
      return;
    }
    if (form.message.trim().length > GB_LIMITS.message) {
      alert(`메시지는 ${GB_LIMITS.message}자 이하로 입력해주세요.`);
      return;
    }
    if (form.password && form.password.length > GB_LIMITS.password) {
      alert(`비밀번호는 ${GB_LIMITS.password}자 이하로 입력해주세요.`);
      return;
    }
    setSubmitting(true);
    try {
      await addGuestbook({
        guestNm: form.guestNm.trim(),
        message: form.message.trim(),
        password: form.password.trim() || null,
      });
      setForm({ guestNm: '', message: '', password: '' });
      await loadGuestbook();
    } catch (e) {
      console.error('방명록 작성 실패', e);
      alert('작성에 실패했습니다. 잠시 후 다시 시도해주세요.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    const password = window.prompt('작성 시 입력한 비밀번호를 입력해주세요.');
    if (password === null) return;
    try {
      await deleteGuestbook(id, password);
      await loadGuestbook();
    } catch (e) {
      alert('삭제할 수 없습니다. (비밀번호 불일치)');
    }
  };

  // ── 이벤트 업로드 ──
  const handleEventFormChange = (e) => {
    const { name, value } = e.target;
    setEventForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileSelect = (e) => {
    const selected = Array.from(e.target.files || []);
    if (selected.length === 0) return;
    if (selected.length > UPLOAD_LIMITS.maxFiles) {
      alert(`최대 ${UPLOAD_LIMITS.maxFiles}개까지 첨부할 수 있어요.`);
      return;
    }
    const tooBig = selected.find((f) => f.size > UPLOAD_LIMITS.maxSize);
    if (tooBig) {
      alert(`'${tooBig.name}' 은(는) 1GB를 초과해요. 더 작은 파일로 올려주세요.`);
      return;
    }
    const badType = selected.find((f) => !/^(image|video)\//.test(f.type));
    if (badType) {
      alert(`'${badType.name}' 은(는) 사진/동영상만 올릴 수 있어요.`);
      return;
    }
    setFiles(selected);
    setUploadStatus('');
  };

  const handleEventSubmit = async () => {
    if (!eventForm.uploaderNm.trim() || !eventForm.contact.trim()) {
      alert('이름과 연락처를 입력해주세요. (당첨자 연락용)');
      return;
    }
    if (files.length === 0) {
      alert('업로드할 사진/동영상을 선택해주세요.');
      return;
    }
    setUploading(true);
    setUploadStatus('업로드 준비 중...');
    try {
      const uploaded = [];
      for (let i = 0; i < files.length; i += 1) {
        const file = files[i];
        const contentType = file.type || 'application/octet-stream';
        setUploadStatus(`업로드 중... (${i + 1}/${files.length})`);
        // 1) presigned URL 발급 (파일명에 업로더 정보 반영되도록 이름/연락처/순번 전달)
        const { data } = await presignUpload({
          fileName: file.name,
          contentType,
          fileSize: file.size,
          uploaderNm: eventForm.uploaderNm.trim(),
          contact: eventForm.contact.trim(),
          seq: i + 1,
        });
        // 2) S3로 직접 PUT (axiosInstance 안 거치고 fetch)
        const res = await fetch(data.uploadUrl, {
          method: 'PUT',
          headers: { 'Content-Type': contentType },
          body: file,
        });
        if (!res.ok) throw new Error('S3 업로드 실패 ' + res.status);
        uploaded.push({
          fileKey: data.fileKey,
          originalNm: file.name,
          contentType,
          fileSize: file.size,
        });
      }
      // 3) 업로드 기록 저장
      await completeUpload({
        uploaderNm: eventForm.uploaderNm.trim(),
        contact: eventForm.contact.trim(),
        files: uploaded,
      });
      setUploadStatus(`완료! ${uploaded.length}개 업로드됐어요 🎉 참여해주셔서 감사합니다!`);
      setFiles([]);
      setFileInputKey((k) => k + 1);
      setEventForm({ uploaderNm: '', contact: '' });
    } catch (err) {
      console.error('업로드 실패', err);
      setUploadStatus('업로드에 실패했어요. 잠시 후 다시 시도해주세요.');
    } finally {
      setUploading(false);
    }
  };

  const addr = encodeURIComponent(WEDDING_INFO.venueAddress);
  const kakaoMapUrl = `https://map.kakao.com/link/search/${addr}`;
  const naverMapUrl = `https://map.naver.com/v5/search/${addr}`;

  const copyAddress = async () => {
    try {
      await navigator.clipboard.writeText(WEDDING_INFO.venueAddress);
      alert('주소가 복사되었습니다.');
    } catch {
      alert(WEDDING_INFO.venueAddress);
    }
  };

  return (
    <div className="wd-root">
      {introVisible && (
        <div
          className={`wd-intro${introFading ? ' wd-intro--fade' : ''}`}
          onClick={dismissIntro}
        >
          <img className="wd-intro-img" src={introGif} alt="봉인식 인트로" />
          <p className="wd-intro-hint">화면을 클릭하면 입장됩니다</p>
        </div>
      )}
      <div className="wd-container">

        {/* 표지 */}
        <section className={`wd-cover${copy.coverImage ? '' : ' wd-cover--plain'}`}>
          {copy.coverImage && <img className="wd-cover-img" src={coverImg} alt="봉인식" />}
          <div className="wd-cover-inner">
            <p className="wd-cover-label">{copy.coverLabel}</p>
            <h1 className="wd-cover-names">
              {copy.coverNames[0]} <span className="wd-amp">{copy.coverNames[1]}</span> {copy.coverNames[2]}
            </h1>
            <p className="wd-cover-sub">{copy.coverSub}</p>
            <p className="wd-cover-date">{WEDDING_INFO.dateText}</p>
            <p className="wd-cover-venue">{WEDDING_INFO.venue}</p>
          </div>
        </section>

        {/* 인사말 */}
        <section className="wd-section">
          <h2 className="wd-title">인사말</h2>
          <p className="wd-greeting">
            {copy.greeting.split('\n').map((line, i) => (
              <span key={i}>{line}<br /></span>
            ))}
          </p>
        </section>

        {/* D-day 카운트다운 */}
        <section className="wd-section">
          <h2 className="wd-title">D-DAY</h2>
          {countdown.done ? (
            <p className="wd-dday-done">{copy.ddayDone}</p>
          ) : (
            <div className="wd-countdown">
              {[
                ['DAYS', countdown.days],
                ['HOUR', countdown.hours],
                ['MIN', countdown.minutes],
                ['SEC', countdown.seconds],
              ].map(([label, value]) => (
                <div className="wd-cd-cell" key={label}>
                  <div className="wd-cd-num">{String(value).padStart(2, '0')}</div>
                  <div className="wd-cd-label">{label}</div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* 예식 안내 */}
        <section className="wd-section">
          <h2 className="wd-title">{copy.infoTitle}</h2>
          <div className="wd-info-row"><span className="wd-info-key">일시</span><span>{WEDDING_INFO.dateText}</span></div>
          <div className="wd-info-row"><span className="wd-info-key">장소</span><span>{WEDDING_INFO.venue} {WEDDING_INFO.venueHall}</span></div>
        </section>

        {/* 오시는 길 */}
        <section className="wd-section">
          <h2 className="wd-title">오시는 길</h2>
          <div className="wd-map-kakao">
            <div
              id={`daumRoughmapContainer${WEDDING_INFO.kakaoMap.timestamp}`}
              className="root_daum_roughmap root_daum_roughmap_landing"
            />
          </div>
          <div className="wd-map-btns">
            <a className="wd-map-btn" href={kakaoMapUrl} target="_blank" rel="noreferrer">카카오맵</a>
            <a className="wd-map-btn" href={naverMapUrl} target="_blank" rel="noreferrer">네이버지도</a>
            <button className="wd-map-btn" type="button" onClick={copyAddress}>주소 복사</button>
          </div>
        </section>

        {/* 참여 버튼 */}
        <section className="wd-section">
          <h2 className="wd-title">함께해주세요</h2>
          <div className="wd-action-btns">
            <button className="wd-action-btn" type="button" onClick={() => setGuestbookOpen(true)}>
              📖 방명록 남기기
            </button>
            <button className="wd-action-btn wd-action-btn--event" type="button" onClick={() => setEventOpen(true)}>
              🎁 이벤트 참여하기
            </button>
          </div>
        </section>

        <footer className="wd-footer">
          {WEDDING_INFO.groom} &amp; {WEDDING_INFO.bride}
        </footer>
      </div>

      {/* 방명록 모달 */}
      <Modal open={guestbookOpen} onClose={() => setGuestbookOpen(false)} title="방명록">
        <form className="wd-gb-form" onSubmit={handleSubmit}>
          <input
            className="wd-input"
            name="guestNm"
            placeholder="이름"
            value={form.guestNm}
            onChange={handleChange}
            maxLength={GB_LIMITS.name}
          />
          <textarea
            className="wd-textarea"
            name="message"
            placeholder={copy.gbPlaceholder}
            value={form.message}
            onChange={handleChange}
            rows={3}
            maxLength={GB_LIMITS.message}
          />
          <input
            className="wd-input"
            name="password"
            type="password"
            placeholder="비밀번호 (삭제 시 필요, 선택)"
            value={form.password}
            onChange={handleChange}
            maxLength={GB_LIMITS.password}
          />
          <button className="wd-submit" type="submit" disabled={submitting}>
            {submitting ? '등록 중...' : '메시지 남기기'}
          </button>
        </form>

        <div className="wd-gb-list">
          {loading ? (
            <p className="wd-gb-empty">불러오는 중...</p>
          ) : entries.length === 0 ? (
            <p className="wd-gb-empty">{copy.gbEmpty}</p>
          ) : (
            entries.map((it) => (
              <div className="wd-gb-item" key={it.guestbookId}>
                <div className="wd-gb-head">
                  <span className="wd-gb-name">{it.guestNm}</span>
                  <span className="wd-gb-date">({formatDate(it.createdAt)})</span>
                </div>
                <p className="wd-gb-msg">{it.message}</p>
                <button className="wd-gb-del" type="button" onClick={() => handleDelete(it.guestbookId)}>
                  삭제
                </button>
              </div>
            ))
          )}
        </div>
      </Modal>

      {/* 이벤트 참여 모달 */}
      <Modal open={eventOpen} onClose={() => (uploading ? null : setEventOpen(false))} title="📸 게릴라 포토 이벤트">
        <div className="wd-event-desc">
          <p>식 당일 찍어주신 <b>사진·동영상</b>을 올려주세요!</p>
          <p>신랑·신부가 직접 <b>베스트 컷</b>을 선별해 상품권을 드립니다 🎉</p>
          <div className="wd-event-prizes">
            <div className="wd-prize"><span className="wd-prize-rank">🥇 1등</span><span>배민 5만원</span></div>
            <div className="wd-prize"><span className="wd-prize-rank">🥈 2등</span><span>배민 3만원</span></div>
            <div className="wd-prize"><span className="wd-prize-rank">🥉 3등</span><span>배민 1만원</span></div>
          </div>
          <p className="wd-event-note">* 당첨자 연락을 위해 이름·연락처를 남겨주세요.</p>
        </div>

        <div className="wd-gb-form">
          <input
            className="wd-input"
            name="uploaderNm"
            placeholder="이름"
            value={eventForm.uploaderNm}
            onChange={handleEventFormChange}
            maxLength={20}
            disabled={uploading}
          />
          <input
            className="wd-input"
            name="contact"
            placeholder="연락처 (전화번호 또는 카톡 ID)"
            value={eventForm.contact}
            onChange={handleEventFormChange}
            maxLength={50}
            disabled={uploading}
          />

          <label className="wd-file-label">
            <input
              key={fileInputKey}
              className="wd-file-input"
              type="file"
              accept="image/*,video/*"
              multiple
              onChange={handleFileSelect}
              disabled={uploading}
            />
            <span>📎 사진·동영상 선택 (최대 {UPLOAD_LIMITS.maxFiles}개 / 파일당 1GB)</span>
          </label>

          {files.length > 0 && (
            <ul className="wd-file-list">
              {files.map((f, i) => (
                <li key={i} className="wd-file-item">
                  <span className="wd-file-name">{f.name}</span>
                  <span className="wd-file-size">{(f.size / (1024 * 1024)).toFixed(1)}MB</span>
                </li>
              ))}
            </ul>
          )}

          <button className="wd-submit" type="button" onClick={handleEventSubmit} disabled={uploading}>
            {uploading ? '업로드 중...' : '업로드하고 참여하기'}
          </button>

          {uploadStatus && <p className="wd-upload-status">{uploadStatus}</p>}
        </div>
      </Modal>
    </div>
  );
}
